import * as React from 'react';
import { RemovalException, RemovalSeverity, RemovalFailureReason, getHandleableException } from 'types/Exceptions';
import Confirm, { ConfirmColor, RenderButtonProps } from '../Confirm';
import { Button } from 'reactstrap';
import Spinner from '../Spinner';
import { AxiosError } from 'axios';
import { RemovalErrorCode } from 'types/ExceptionCodes';
import Alert from '../Alert';

type RenderButtonDelegate = (props: RenderButtonProps) => React.ReactNode;

interface EditErrorAlertPopupProps {
	/** The removal error, coming from the server. Contains information on why it failed, the severity of the attempt, and whether it can be overridden. */
	axiosError: AxiosError | null;

	/** Called when the confirm button is pressed. */
	onConfirm: () => void;

	/** Called when the cancel button is pressed or the alert is closed through other means. */
	onCancel: () => void;

	/** Defines the types of buttons that will appear on the alert. */
	renderButtons?: 'Accept' | 'NoAccept' | 'TimedAccept' | RenderButtonDelegate;

	/** The title text of the alert. */
	title?: () => React.ReactNode;

	/** Whether the alert is being displayed right now. */
	isOpen: boolean;

	/** Whether there should be a spinner on the submit button, indicating that the server is handling the submission request. */
	inProgress: boolean;
}

interface EditErrorAlertPopupState {
	/** The time remaining until the submit button is enabled. */
	lockTime: number;
}

const DANGER_LOCK_TIME = 5;
const CONFIRM_TEXT = 'Yes, I understand';

/**
 * The alert that displays to a user when they are attempting to remove a record with active dependencies/foreign keys.
 * This will display an error message informing the user what they are about to do or were trying to do and provide them
 * an option to force the change if possible.
 *
 * @class EditErrorAlertPopup
 * @extends {React.Component<EditErrorAlertPopupProps, EditErrorAlertPopupState>}
 */
class EditErrorAlertPopup extends React.Component<EditErrorAlertPopupProps, EditErrorAlertPopupState> {
	public static defaultProps = {
		onCancel: () => {},
	};

	constructor(props) {
		super(props);

		this.state = {
			lockTime: DANGER_LOCK_TIME,
		};
	}

	/** The timer that counts down the lock time */
	private lockTimer: NodeJS.Timeout | null = null;

	public componentDidMount() {
		if (this.props.isOpen) {
			this.setLockTimer();
		}
	}

	public componentDidUpdate(prevProps: EditErrorAlertPopupProps) {
		if (this.props.isOpen && !prevProps.isOpen) {
			this.setLockTimer();
		}
	}

	public componentWillUnmount() {
		this.killLockTimer();
	}

	/** Sets the lock time to the specified value and starts up the lock timer if it doesn't exist already */
	private setLockTimer(time = DANGER_LOCK_TIME) {
		this.killLockTimer();
		this.lockTimer = setInterval(() => this.lockTimerTick(), 1000);
		this.setState({ lockTime: time });
	}

	/** Kills the lock timer if it exists */
	private killLockTimer() {
		if (this.lockTimer) {
			clearInterval(this.lockTimer);
			this.lockTimer = null;
		}
	}

	/** Decrements the lock timer and kills the timer once the timer has run out */
	private lockTimerTick = () => {
		if (this.state.lockTime <= 1) {
			this.killLockTimer();
		}

		if (this.state.lockTime > 0) {
			this.setState((prevState) => ({
				lockTime: prevState.lockTime - 1,
			}));
		}
	};

	/** Confirm button render method. Renders only a cancel button on the alert, for cases where forcing isn't possible */
	private renderNoAcceptButton = (renderButtonProps: RenderButtonProps) => {
		return (
			<div className="text-center">
				<Button
					color={renderButtonProps.cancelButtonColor}
					className="btn-inline btn-modal-action fancy-modal__cancel-btn"
					outline
					onClick={renderButtonProps.onCancel}
					data-testid={renderButtonProps.cancelTestId}
				>
					{renderButtonProps.cancelButtonText}
				</Button>
			</div>
		);
	};

	/** Confirm button render method. Renders an accept and cancel button with the accept button disabled until a certain amount of time has passed */
	private renderTimedAcceptButton = (renderButtonProps: RenderButtonProps) => {
		return (
			<div className="text-center">
				<Button
					color={renderButtonProps.cancelButtonColor}
					className="btn-inline btn-modal-action fancy-modal__cancel-btn"
					outline
					onClick={renderButtonProps.onCancel}
					data-testid={renderButtonProps.cancelTestId}
				>
					{renderButtonProps.cancelButtonText}
				</Button>
				<Button
					color={renderButtonProps.confirmButtonColor}
					className="btn-modal-action fancy-modal__confirm-btn"
					outline={renderButtonProps.color === 'primary' ? true : false}
					onClick={renderButtonProps.onConfirm}
					disabled={renderButtonProps.inProgress || this.state.lockTime > 0}
					data-testid={renderButtonProps.confirmTestId}
				>
					{renderButtonProps.inProgress ? (
						<Spinner centered color="light" />
					) : this.state.lockTime > 0 ? (
						`${renderButtonProps.confirmButtonText} (${this.state.lockTime}...)`
					) : (
						renderButtonProps.confirmButtonText
					)}
				</Button>
			</div>
		);
	};

	private getColor() {
		const exception = this.props.axiosError && (getHandleableException(this.props.axiosError) as RemovalException);
		if (exception && exception.exceptionCode === RemovalErrorCode) {
			switch (exception.removalSeverity) {
				default:
				case RemovalSeverity.Normal:
					return 'default';

				case RemovalSeverity.Warning:
					return 'warning';

				case RemovalSeverity.Danger:
					return 'danger';
			}
		}

		return 'warning';
	}

	public render() {
		const { axiosError, isOpen, inProgress, title, onCancel, onConfirm, renderButtons } = this.props;
		const exception = this.props.axiosError && getHandleableException(axiosError);
		const isClientHandleable = exception != null;

		const color: ConfirmColor = this.getColor();

		const renderContentsFunc = () => {
			const loggingErrorId =
				(axiosError &&
					axiosError.response &&
					axiosError.response.data &&
					(axiosError.response.data.loggingErrorId as string)) ||
				null;
			if (isClientHandleable) {
				return exception
					? exception.message.split('\n').map((line, i) => (
							<p key={'message-line-' + i} data-testid={'edit-error-message-' + i}>
								{line}
							</p>
					  ))
					: null;
			} else {
				return (
					<div>
						<p>The server encountered an error while processing your request.</p>
						{loggingErrorId ? <p>ID: {loggingErrorId}</p> : null}
					</div>
				);
			}
		};

		const boundRenderNoAcceptButton = this.renderNoAcceptButton;
		const boundRenderTimedAcceptButton = this.renderTimedAcceptButton;

		const renderButtonsFunc = () => {
			switch (renderButtons) {
				case 'Accept':
					return undefined;
				case 'NoAccept':
					return boundRenderNoAcceptButton;
				case 'TimedAccept':
					return boundRenderTimedAcceptButton;
			}

			const removalException =
				(exception && exception.exceptionCode === RemovalErrorCode && (exception as RemovalException)) || null;

			return removalException && removalException.failureReason !== RemovalFailureReason.WasNotForced
				? boundRenderNoAcceptButton
				: removalException && removalException.removalSeverity === RemovalSeverity.Danger
				? boundRenderTimedAcceptButton
				: undefined;
		};

		if (!isClientHandleable) {
			return (
				<Alert
					isOpen={isOpen}
					color={color}
					renderContents={renderContentsFunc}
					renderTitle={title || (() => 'Removal Error!')}
					onConfirm={onCancel}
				/>
			);
		}

		return (
			<Confirm
				color={color}
				isOpen={isOpen}
				onCancel={onCancel}
				onConfirm={onConfirm}
				renderTitle={title || (() => 'Removal Error!')}
				renderContents={renderContentsFunc}
				renderButtons={renderButtonsFunc()}
				confirmButtonText={CONFIRM_TEXT}
				inProgress={inProgress}
				cancelTestId="removal-error-alert-cancel"
				confirmTestId="removal-error-alert-confirm"
			/>
		);
	}
}

export default EditErrorAlertPopup;
